import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import VinculacionModal from './VinculacionModal';
import { emitTareaChanged } from '../utils/tareasBus';

const emptyForm = () => ({
  titulo: '', descripcion: '', fecha: '', fecha_vencimiento: '',
  prioridad: 'normal', expediente_id: '', tramite_id: '',
});

export default function TareaModal({ open, onClose, editing = null, onSaved, defaultFecha = '', defaultExpedienteId = '', defaultTramiteId = '' }) {
  const [expedientes, setExpedientes] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [vincularOpen, setVincularOpen] = useState(false);

  const editingId = editing?.id || null;
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [exp, tra] = await Promise.all([
        supabase.from('expedientes').select('id, numero, caratula').eq('eliminado', false).order('caratula'),
        supabase.from('tramites').select('id, nombre').eq('eliminado', false).order('nombre'),
      ]);
      setExpedientes(exp.data || []);
      setTramites(tra.data || []);
    })();
    if (editing) {
      setForm({
        titulo: editing.titulo || '', descripcion: editing.descripcion || '',
        fecha: editing.fecha || '', fecha_vencimiento: editing.fecha_vencimiento || '',
        prioridad: editing.prioridad || 'normal',
        expediente_id: editing.expediente_id || '', tramite_id: editing.tramite_id || '',
      });
    } else {
      setForm({ ...emptyForm(), fecha: defaultFecha || '', expediente_id: defaultExpedienteId || '', tramite_id: defaultTramiteId || '' });
    }
  }, [open, editing, defaultFecha, defaultExpedienteId, defaultTramiteId]);

  // Etiqueta del expediente/trámite vinculado
  let vinculo = null;
  if (form.expediente_id) {
    const e = expedientes.find(x => x.id === form.expediente_id);
    vinculo = { icon: '📁', tipo: 'Expediente', label: e ? `${e.numero ? e.numero + ' — ' : ''}${e.caratula || ''}` : 'Expediente' };
  } else if (form.tramite_id) {
    const t = tramites.find(x => x.id === form.tramite_id);
    vinculo = { icon: '📋', tipo: 'Trámite', label: t ? t.nombre : 'Trámite' };
  }

  async function saveTarea() {
    if (guardando) return;
    if (!form.titulo.trim()) { showToast('El título es obligatorio'); return; }
    setGuardando(true);
    const tareaData = {
      titulo: form.titulo, descripcion: form.descripcion || null,
      fecha: form.fecha || null, fecha_vencimiento: form.fecha_vencimiento || null,
      prioridad: form.prioridad,
      expediente_id: form.expediente_id || null,
      tramite_id: form.tramite_id || null,
      completada: false,
    };
    let error;
    if (editingId) {
      ({ error } = await supabase.from('tareas').update(tareaData).eq('id', editingId));
    } else {
      const result = await supabase.from('tareas').insert(tareaData).select('id').single();
      error = result.error;
    }
    setGuardando(false);
    if (error) { showToast('Error al guardar'); return; }
    if (editingId) emitTareaChanged({ id: editingId, completada: false });
    onSaved?.();
    onClose();
  }

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay" style={{ zIndex: 900 }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ width: 520, maxHeight: 'none' }}>
          <div className="modal-header">
            <div className="modal-title">{editingId ? 'Editar tarea' : 'Nueva tarea'}</div>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body" style={{ overflow: 'visible', padding: '12px 22px' }}>
            <div className="form-grid" style={{ gap: 9 }}>
              <div className="form-group form-full">
                <label className="form-label">Título *</label>
                <input className="form-input" value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Descripción de la tarea" autoFocus />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Descripción</label>
                <textarea className="form-textarea" value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Detalles adicionales..." style={{ resize: 'none', minHeight: 48 }} />
              </div>
              <div className="form-full" style={{ display: 'flex', gap: 9 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Fecha de Realización</label>
                  <input className="form-input" type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Vencimiento</label>
                  <input className="form-input" type="date" value={form.fecha_vencimiento} onChange={e => setForm(f => ({ ...f, fecha_vencimiento: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Prioridad</label>
                  <select className="form-select" value={form.prioridad} onChange={e => setForm(f => ({ ...f, prioridad: e.target.value }))}>
                    <option value="urgente">Urgente</option>
                    <option value="muy importante">Muy importante</option>
                    <option value="normal">Normal</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
              </div>

              {/* Expediente / Trámite vinculado */}
              <div className="form-group form-full">
                <label className="form-label">Expediente / Trámite</label>
                {vinculo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{vinculo.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: 'var(--text4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{vinculo.tipo}</div>
                      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vinculo.label}</div>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVincularOpen(true)} style={{ flexShrink: 0 }}>Cambiar</button>
                  </div>
                ) : (
                  <button className="btn btn-ghost" onClick={() => setVincularOpen(true)} style={{ width: '100%', justifyContent: 'center' }}>
                    + Vincular expediente o trámite
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={saveTarea} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar tarea'}</button>
          </div>
        </div>
      </div>

      <VinculacionModal
        open={vincularOpen}
        onClose={() => setVincularOpen(false)}
        tabs={['expedientes', 'tramites']}
        value={{ expediente_id: form.expediente_id, tramite_id: form.tramite_id }}
        onSelect={(v) => setForm(f => ({ ...f, expediente_id: v.expediente_id, tramite_id: v.tramite_id }))}
      />

      {toast && <div className="toast" style={{ zIndex: 1200 }}>✓ {toast}</div>}
    </>
  );
}
